import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    LOW_DEMAND_MODE,
    HIGH_DEMAND_MODE,
    CONTINUOUS_MODE,
    EDIT, VIEW, WARNING,
    IEC_61508_1998,
    IEC_61508_2010,
    ISA_TR84_2002,
    ISA_TR84_2022,
    VDI_VDE_2180_2019,
    NO_DANGEROUS_FAILURE,
    NO_SAFE_FAILURE,
} from "const"
import { projectsRoute, silSafetyFunctionsRoute } from "client/parts/routes"
import { SilCalculationMethod } from "client/utils"
import {
    StoreType,
    addModal,
    setSilResultReloadList,
    setSilResultSafetyFunctionList,
} from "client/store"
import {
    Collapse,
    AlertMessage,
    Link,
} from "client/components"

import FormatProbability from "client/pages/sil/format/probability"
import { getMaxProbability } from "client/pages/sil/formulas/internal"
import {
    TabWrapper,
    TabList,
    TabListRow,
    TabContent,
    BlockTitle,
    BlockContent,
    BlockContentRow,
    BlockContentTitle,
} from "./styled"

import Calculation from "./reportContent/calculation"
import Model from "./reportContent/model"
import Configuration from "./reportContent/configuration"
import SubsystemArchitectureParameters from "./reportContent/subsystemArchitectureParameters"

const Report = () => {
    const safetyFunctions = useSelector((state: StoreType) => state.silResultSafetyFunction.safetyFunctionList)
    const calculatedSafetyFunctions = useSelector((state: StoreType) => state.silResultSafetyFunction.calculatedSafetyFunctionList)
    const project = useSelector((state: StoreType) => state.project.projectView)

    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const projectSettings = useSelector((state: StoreType) => state.project.projectSettings)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeFunction, setActiveFunction] = useState(safetyFunctions.length ? safetyFunctions[0] : null)

    // clear result safety functions on unmount
    useEffect(() => () => {
        dispatch(setSilResultSafetyFunctionList([]))
    }, [])

    useEffect(() => {
        if (activeFunction) { // update active function data
            setActiveFunction(safetyFunctions.find((row) => row.id === activeFunction.id))
        } else {
            setActiveFunction(safetyFunctions.length ? safetyFunctions[0] : null)
        }
    }, [safetyFunctions])

    const resultSafetyFunction = activeFunction ? calculatedSafetyFunctions[activeFunction.id] : null

    // calculation method can be or dangerous or safe or "no dangerous/safe failure", never both
    let calculationMethod: SilCalculationMethod = null
    if (resultSafetyFunction) {
        const { calculationMethodDangerous, calculationMethodSafe } = resultSafetyFunction
        if (calculationMethodDangerous !== NO_DANGEROUS_FAILURE) calculationMethod = calculationMethodDangerous
        if (calculationMethodSafe !== NO_SAFE_FAILURE) calculationMethod = calculationMethodSafe
    }

    const safetyFunctionUpdated = lang.safetyFunctionUpdated.split("{0}")
    const updateUrl = `${projectsRoute}/${VIEW}/${project.id}${silSafetyFunctionsRoute}/${EDIT}/${activeFunction && activeFunction.id}`

    const { defaultAssumption } = accountSettings.sil
    const { assumption } = projectSettings.sil

    return (
        <Collapse show title={lang.safetyFunctions}>
            <TabWrapper>
                <TabList>
                    {
                        safetyFunctions.map((safetyFunction) => (
                            <TabListRow
                                key={safetyFunction.id}
                                active={activeFunction && activeFunction.id === safetyFunction.id}
                                onClick={() => setActiveFunction(safetyFunction)}
                            >
                                {safetyFunction.name}
                            </TabListRow>
                        ))
                    }
                </TabList>
                {
                    !resultSafetyFunction && (
                        <TabContent>
                            <BlockTitle>{lang.safetyFunctionNotCalculated}</BlockTitle>
                        </TabContent>
                    )
                }
                {
                    resultSafetyFunction && (
                        <TabContent>
                            <BlockTitle>{activeFunction.name}</BlockTitle>

                            <BlockTitle>{lang.safetyFunctionDescription}</BlockTitle>
                            <BlockContent dangerouslySetInnerHTML={{ __html: activeFunction.description }} />

                            {
                                !activeFunction.calculated && (
                                    <AlertMessage variant={WARNING}>
                                        <b>{lang.warning}</b>
                                        <div>
                                            {safetyFunctionUpdated[0]}
                                            <Link
                                                href={updateUrl}
                                                onClick={() => dispatch(addModal({
                                                    url: updateUrl,
                                                    onClose: () => {
                                                        // reload result list
                                                        dispatch(setSilResultReloadList(false))
                                                        setTimeout(() => {
                                                            dispatch(setSilResultReloadList(true))
                                                        }, 0)
                                                    },
                                                }))}
                                            >
                                                {lang.here}
                                            </Link>
                                            {safetyFunctionUpdated[1]}
                                        </div>
                                    </AlertMessage>
                                )
                            }

                            <BlockTitle>{lang.safetyFunctionParameters}</BlockTitle>
                            <BlockContent>
                                <BlockContentRow>
                                    <BlockContentTitle>{`${lang.calculationMethod}:`}</BlockContentTitle>
                                    { calculationMethod === IEC_61508_2010 && lang.simplifiedEquationsIec_61508_2010 }
                                    { calculationMethod === IEC_61508_1998 && lang.simplifiedEquationsIec_61508_1998 }
                                    { calculationMethod === ISA_TR84_2002 && lang.simplifiedEquationsIsa_tr84_2002 }
                                    { calculationMethod === ISA_TR84_2022 && lang.simplifiedEquationsIsa_tr84_2022 }
                                    { calculationMethod === VDI_VDE_2180_2019 && lang.simplifiedEquationsVdi_vde_2180_2019 }
                                </BlockContentRow>

                                <BlockContentRow>
                                    <BlockContentTitle>{`${lang.mode}:`}</BlockContentTitle>
                                    { resultSafetyFunction.mode === LOW_DEMAND_MODE && lang.modeLowDemand }
                                    { resultSafetyFunction.mode === HIGH_DEMAND_MODE && lang.modeHighDemand }
                                    { resultSafetyFunction.mode === CONTINUOUS_MODE && lang.modeContinuous }
                                </BlockContentRow>

                                {
                                    resultSafetyFunction.calculationMethodDangerous !== NO_DANGEROUS_FAILURE && ([
                                        <BlockContentRow key="targetSil">
                                            <BlockContentTitle>{`${lang.targetSilLevel}:`}</BlockContentTitle>
                                            { resultSafetyFunction.targetSil === null && lang.noTargetSet }
                                            { resultSafetyFunction.targetSil !== null && lang[`sil_${resultSafetyFunction.targetSil}`] }
                                        </BlockContentRow>,
                                        <BlockContentRow key="targetPfd">
                                            {
                                                resultSafetyFunction.mode === LOW_DEMAND_MODE
                                                && <BlockContentTitle>{`${lang.targetPfd}:`}</BlockContentTitle>
                                            }
                                            {
                                                (
                                                    resultSafetyFunction.mode === HIGH_DEMAND_MODE
                                                    || resultSafetyFunction.mode === CONTINUOUS_MODE
                                                ) && <BlockContentTitle>{`${lang.targetPfh}:`}</BlockContentTitle>
                                            }
                                            <FormatProbability data={
                                                resultSafetyFunction.targetPfd !== null
                                                    ? resultSafetyFunction.targetPfd
                                                    : getMaxProbability(resultSafetyFunction.mode, resultSafetyFunction.targetSil)
                                            }
                                            />
                                        </BlockContentRow>,
                                    ])
                                }

                                {
                                    resultSafetyFunction.calculationMethodSafe !== NO_SAFE_FAILURE
                                    && resultSafetyFunction.targetStl !== null
                                    && ([
                                        <BlockContentRow key="targetStl">
                                            <BlockContentTitle>{`${lang.targetStlLevel}:`}</BlockContentTitle>
                                            {lang.stlLevel.replace("{0}", resultSafetyFunction.targetStl.toString())}
                                        </BlockContentRow>,
                                        <BlockContentRow key="targetStr">
                                            <BlockContentTitle>{`${lang.targetStr}:`}</BlockContentTitle>
                                            <FormatProbability data={
                                                resultSafetyFunction.targetStr !== null
                                                    ? resultSafetyFunction.targetStr
                                                    : 0
                                            }
                                            />
                                        </BlockContentRow>,
                                    ])
                                }
                            </BlockContent>


                            <BlockTitle>{lang.results}</BlockTitle>
                            <Calculation resultSafetyFunction={resultSafetyFunction} />


                            <BlockTitle>{lang.architecture}</BlockTitle>
                            <Model resultSafetyFunction={resultSafetyFunction} />


                            <BlockTitle>{lang.configuration}</BlockTitle>
                            <Configuration resultSafetyFunction={resultSafetyFunction} />


                            <BlockTitle>{lang.subsystemArchitectureParameters}</BlockTitle>
                            <SubsystemArchitectureParameters resultSafetyFunction={resultSafetyFunction} />

                            <BlockTitle>{lang.calculationAssumptions}</BlockTitle>
                            <BlockContent dangerouslySetInnerHTML={{ __html: assumption || defaultAssumption }} />
                        </TabContent>
                    )
                }
            </TabWrapper>
        </Collapse>
    )
}
export default Report
